import * as React from 'react';
import classNames from 'classnames';
import RCPicker from 'rc-picker';

import enUS from '../locale/en_US';
import { getPlaceholder } from '../util';
import devWarning from '../_util/devWarning';

import Icon, {
    ICON_TYPE_CALENDAR,
    ICON_TYPE_CLOSE,
} from '../../../Icon';

import PickerButton from '../PickerButton';

const Components = { button: PickerButton };

function toArray<T>(list: T | T[] | undefined): T[] {
    if (!list) return [];
    return Array.isArray(list) ? list : [list];
}

interface TimeProps {
    format?: string | string[];
    picker?: string;
    showHour?: boolean;
    showMinute?: boolean;
    showSecond?: boolean;
    use12Hours?: boolean;
}

function getTimeProps(props: TimeProps) {
    const {
        format,
        picker,
        showHour,
        showMinute,
        showSecond,
        use12Hours,
    } = props;

    const firstFormat = toArray(format)[0];
    const showTimeObj: any = { ...props };

    if (firstFormat) {
        if (!firstFormat.includes('s') && showSecond === undefined) {
            showTimeObj.showSecond = false;
        }
        if (!firstFormat.includes('m') && showMinute === undefined) {
            showTimeObj.showMinute = false;
        }
        if (
            !firstFormat.includes('H') &&
            !firstFormat.includes('h') &&
            showHour === undefined
        ) {
            showTimeObj.showHour = false;
        }
        if (
            (firstFormat.includes('a') ||
                firstFormat.includes('A')) &&
            use12Hours === undefined
        ) {
            showTimeObj.use12Hours = true;
        }
    }

    if (picker === 'time') {
        return showTimeObj;
    }

    return {
        showTime: showTimeObj,
    };
}

/**
 * NOTE:
 * generateConfig is intentionally kept as any because rc-picker configs
 * vary widely (moment/dayjs/date-fns adapters)
 */
export default function generatePicker<TDate>(
    generateConfig: any
) {
    function getPicker(
        picker?: string,
        displayName?: string
    ) {
        type Props = any;

        class Picker extends React.Component<Props> {
            pickerRef: React.RefObject<any>;

            constructor(props: Props) {
                super(props);
                this.pickerRef = React.createRef();

                this.focus = this.focus.bind(this);
                this.blur = this.blur.bind(this);
            }

            focus() {
                this.pickerRef.current?.focus?.();
            }

            blur() {
                this.pickerRef.current?.blur?.();
            }

            getDefaultLocale() {
                const { locale } = this.props;

                const result: any = {
                    ...enUS,
                    ...locale,
                };

                result.lang = {
                    ...result.lang,
                    ...(locale || {}).lang,
                };

                return result;
            }

            renderPicker(locale: any) {
                const getPrefixCls = (suffixCls?: string) =>
                    suffixCls
                        ? `additiv-${suffixCls}`
                        : 'additiv';

                const {
                    direction,
                    getPopupContainer,
                } = this.context as any;

                const {
                    prefixCls: customizePrefixCls,
                    getPopupContainer: customizeGetPopupContainer,
                    className,
                    size: customizeSize,
                    bordered = true,
                    placeholder,
                    showTime,
                    ...restProps
                } = this.props;

                const mergedPicker = picker || this.props.picker;

                const prefixCls = getPrefixCls('picker');

                const additionalProps: any = {
                    showToday: true,
                };

                let additionalOverrideProps: any = {};

                if (picker) {
                    additionalOverrideProps.picker = picker;
                }

                additionalOverrideProps = {
                    ...additionalOverrideProps,
                    ...(showTime
                        ? getTimeProps({
                              format: this.props.format,
                              picker: mergedPicker,
                              ...showTime,
                          })
                        : {}),
                    ...(mergedPicker === 'time'
                        ? getTimeProps({
                              format: this.props.format,
                              ...this.props,
                              picker: mergedPicker,
                          })
                        : {}),
                };

                return (
                    <RCPicker
                        ref={this.pickerRef}
                        placeholder={getPlaceholder(
                            mergedPicker,
                            locale,
                            placeholder
                        )}
                        suffixIcon={
                            <Icon
                                type={ICON_TYPE_CALENDAR}
                                size={10}
                            />
                        }
                        allowClear={{
                            clearIcon: (
                                <Icon
                                    type={ICON_TYPE_CLOSE}
                                    size={10}
                                />
                            ),
                        }}
                        transitionName="slide-up"
                        {...additionalProps}
                        {...restProps}
                        {...additionalOverrideProps}
                        locale={locale.lang}
                        className={classNames(className, {
                            [`${prefixCls}-${customizeSize}`]:
                                customizeSize,
                            [`${prefixCls}-borderless`]:
                                !bordered,
                        })}
                        prefixCls={prefixCls}
                        getPopupContainer={
                            customizeGetPopupContainer ||
                            getPopupContainer
                        }
                        generateConfig={generateConfig}
                        prevIcon={
                            <span
                                className={`${prefixCls}-prev-icon`}
                            />
                        }
                        nextIcon={
                            <span
                                className={`${prefixCls}-next-icon`}
                            />
                        }
                        superPrevIcon={
                            <span
                                className={`${prefixCls}-super-prev-icon`}
                            />
                        }
                        superNextIcon={
                            <span
                                className={`${prefixCls}-super-next-icon`}
                            />
                        }
                        components={Components}
                        direction={direction}
                    />
                );
            }

            render() {
                const locale = this.getDefaultLocale();
                return this.renderPicker(locale);
            }
        }

        if (displayName) {
            Picker.displayName = displayName;
        }

        return Picker;
    }

    const DatePicker = getPicker();
    const WeekPicker = getPicker('week', 'WeekPicker');
    const MonthPicker = getPicker('month', 'MonthPicker');
    const YearPicker = getPicker('year', 'YearPicker');
    const QuarterPicker = getPicker(
        'quarter',
        'QuarterPicker'
    );

    return {
        DatePicker,
        WeekPicker,
        MonthPicker,
        YearPicker,
        QuarterPicker,
    };
}